import React, { useState } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import EqualizerIcon from '@mui/icons-material/InsertChartOutlined';
import { ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Area } from 'recharts'

import { AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState'
import { getStaticStability } from '../lib/mr-bullet.js'

function GyroscopicStability() {
  const [ velocity, setVelocity ] = useAutoSaveState('velocity', '2800')
  const [ diameter, setDiameter ] = useAutoSaveState('diameter', '.308')
  const [ altitude, setAltitude ] = useAutoSaveState('altitude', '0')
  const [ Ix, setIx ] = useAutoSaveState('Ix', '.9')
  const [ Iy, setIy ] = useAutoSaveState('Iy', '12')
  const [ Cma, setCma ] = useAutoSaveState('Cma', '4')
  const [ preview, setPreview ] = useAutoSaveState('preview', null)
  const [ result, setResult ] = useState(null)

  const calculate = () => {
    const data = []
    for (let twistRate = 1; twistRate <= 30; twistRate += .1) {
      const sg = getStaticStability(
        parseFloat(diameter),
        parseFloat(twistRate),
        parseFloat(Cma),
        parseFloat(Iy),
        parseFloat(Ix),
        parseFloat(velocity),
        parseFloat(altitude)
      )
      if (sg > .5 && sg < 3) {
        data.push({
          sg,
          twistRate: twistRate.toFixed(1),
          unstable: [ 0, 1, sg < 1 ],
          marginal: [ 1, 1.5, sg >= 1 && sg < 1.5 ],
          stable: [ 1.5, 3, sg >= 1.5 ],
        })
      }
    }
    const result = { data }
    if (data.length > 0) {
      const minTwistData = data.filter(v => v.sg > 1.5).pop()
      if (minTwistData && minTwistData.twistRate) {
        result.minTwist = minTwistData.twistRate
      }
    }
    console.log(result)
    setResult(result)
    setPreview(`Min Twist: ${result.minTwist}`)
    window.gtag('event', 'calculate-gyroscopic-stability')
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3} justifyContent="center" >
        <Grid container justifyContent="center" xs={12} style={{ position: 'relative' }}>
          <Typography variant="h2" component="h1" gutterBottom>
            Gyroscopic Stability Calculator
          </Typography>
          <SaveButton />
        </Grid>
        <NameField />
        <Grid item xs={8}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body1" gutterBottom color="text.secondary">
                  This is the advanced gyroscopic stability calculator.
                  If these inputs are unfamiliar to you, you may be looking for the <a href="/tools/twist-rate-calculator">Twist Rate Calculator</a>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Bullet Velocity" value={velocity} onChange={e => setVelocity(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">fps</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Bullet Diameter" value={diameter} onChange={e => setDiameter(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">in</InputAdornment> }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Altitude" value={altitude} onChange={e => setAltitude(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Ix" value={Ix} onChange={e => setIx(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">g.cm<sup>2</sup></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Iy" value={Iy} onChange={e => setIy(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">g.cm<sup>2</sup></InputAdornment> }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Cmα" value={Cma} onChange={e => setCma(e.target.value)} />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={8}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container xs={12} justifyContent="center">
              <Button variant="contained" onClick={calculate}>Calculate</Button>
            </Grid>
          </Paper>
        </Grid>
        {result && result.data.length > 0 && <Grid item xs={8}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '500px' }}>
            <Grid container xs={12} justifyContent="center">
              <Typography variant="h5" gutterBottom>
                <EqualizerIcon /> Results
              </Typography>
            </Grid>
            <Grid container xs={12} justifyContent="center">
              <Typography variant="h4" gutterBottom>
                Minimum Twist Rate: {result.minTwist}
              </Typography>
            </Grid>
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={result.data} margin={{ top: 0, right: 20, left: 20, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="twistRate" label={{ value: 'Twist Rate', position: 'bottom' }} />
                <YAxis label={{ value: 'SG', angle: -90, position: 'insideLeft' }} />
                <Area name="" type="monotone" dataKey="unstable" fill="#ff0000" strokeWidth="0" opacity="0.5" />
                <Area name="" type="monotone" dataKey="marginal" fill="#ffff00" strokeWidth="0" opacity="0.5" />
                <Area name="" type="monotone" dataKey="stable" fill="#228b22" strokeWidth="0" opacity="0.5" />
                <Line type="monotone" dataKey="sg" dot={false} stroke="#36A2EB" strokeWidth={2} />
                <Tooltip
                  filterNull={true}
                  formatter={(value, name, ...args) => {
                    if (name === 'sg') return value.toFixed(2)
                    if (name === 'stable') return value[2] ? 'yes' : null
                    if (name === 'marginal') return value[2] ? 'yes' : null
                    if (name === 'unstable') return value[2] ? 'yes' : null
                    return null
                  }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>}
        {result && result.data.length === 0 && <Grid item xs={8}>
          <Paper sx={{ p: 2 }}>
            <Grid container xs={12} justifyContent="center">
              <Typography variant="h5" gutterBottom>
                Error: no valid twist rate found
              </Typography>
            </Grid>
          </Paper>
        </Grid>}
      </Grid>
    </Container>
  );
}

export default function WrappedGyroscopicStability() {
  return (
    <AutoSaveProvider>
      <GyroscopicStability />
    </AutoSaveProvider>
  );
}
